import { API, graphqlOperation } from 'aws-amplify';

const responseCache = new Map();
const CACHE_TTL = 60000;

const cached = (key, loader) => {
  const current = responseCache.get(key);
  if (current && current.expiresAt > Date.now()) return current.promise;
  const promise = Promise.resolve().then(loader).catch((error) => { responseCache.delete(key); throw error; });
  responseCache.set(key, { promise, expiresAt: Date.now() + CACHE_TTL });
  return promise;
};

const clearContentCache = () => responseCache.clear();

const instructorFields = `
  id name slug bio country styles profileImageUrl headerImageUrl
  instagramUrl youtubeUrl websiteUrl isFeatured status sortOrder
  createdAt updatedAt
`;

const courseFields = `
  id title slug shortDescription description coverImageUrl trailerUrl
  difficulty accessLevel tags instructorIDs isFeatured status sortOrder
  createdAt updatedAt
`;

const moduleFields = `
  id courseID title description status sortOrder createdAt updatedAt
`;

const legacyLessonFields = `
  id courseID moduleID title slug description previewVideoUrl previewVideoProvider videoUrl videoProvider
  thumbnailUrl durationMinutes isFreePreview isFeatured status sortOrder createdAt updatedAt
`;
const lessonFields = `${legacyLessonFields} videoParts`;
const lessonPlayerFields = `${lessonFields} captionUrl captionLabel`;
const legacyLessonPlayerFields = `${legacyLessonFields} captionUrl captionLabel`;

const LIST_INSTRUCTORS = /* GraphQL */ `
  query ContentListInstructors($filter: ModelInstructorFilterInput, $limit: Int, $nextToken: String) {
    listInstructors(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items { ${instructorFields} }
      nextToken
    }
  }
`;

const INSTRUCTORS_BY_SLUG = /* GraphQL */ `
  query ContentInstructorsBySlug($slug: String!, $limit: Int) {
    instructorsBySlug(slug: $slug, limit: $limit) {
      items { ${instructorFields} }
    }
  }
`;

const LIST_COURSES = /* GraphQL */ `
  query ContentListCourses($filter: ModelCourseFilterInput, $limit: Int, $nextToken: String) {
    listCourses(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items { ${courseFields} }
      nextToken
    }
  }
`;

const COURSES_BY_SLUG = /* GraphQL */ `
  query ContentCoursesBySlug($slug: String!, $limit: Int) {
    coursesBySlug(slug: $slug, limit: $limit) {
      items { ${courseFields} }
    }
  }
`;

const LIST_MODULES = /* GraphQL */ `
  query ContentListModules($filter: ModelCourseModuleFilterInput, $limit: Int, $nextToken: String) {
    listCourseModules(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items { ${moduleFields} }
      nextToken
    }
  }
`;

const MODULES_BY_COURSE = /* GraphQL */ `
  query ContentModulesByCourse($courseID: ID!, $sortDirection: ModelSortDirection, $limit: Int) {
    modulesByCourse(courseID: $courseID, sortDirection: $sortDirection, limit: $limit) {
      items { ${moduleFields} }
    }
  }
`;

const LIST_LESSONS = /* GraphQL */ `
  query ContentListLessons($filter: ModelLessonFilterInput, $limit: Int, $nextToken: String) {
    listLessons(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items { ${lessonFields} }
      nextToken
    }
  }
`;

const LIST_LESSONS_LEGACY = /* GraphQL */ `
  query ContentListLessonsLegacy($filter: ModelLessonFilterInput, $limit: Int, $nextToken: String) {
    listLessons(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items { ${legacyLessonFields} }
      nextToken
    }
  }
`;

const LESSONS_BY_COURSE = /* GraphQL */ `
  query ContentLessonsByCourse($courseID: ID!, $sortDirection: ModelSortDirection, $limit: Int) {
    lessonsByCourse(courseID: $courseID, sortDirection: $sortDirection, limit: $limit) {
      items { ${lessonFields} }
    }
  }
`;

const LESSONS_BY_COURSE_LEGACY = /* GraphQL */ `
  query ContentLessonsByCourseLegacy($courseID: ID!, $sortDirection: ModelSortDirection, $limit: Int) {
    lessonsByCourse(courseID: $courseID, sortDirection: $sortDirection, limit: $limit) {
      items { ${legacyLessonFields} }
    }
  }
`;

const LESSONS_BY_SLUG = /* GraphQL */ `
  query ContentLessonsBySlug($slug: String!, $limit: Int) {
    lessonsBySlug(slug: $slug, limit: $limit) {
      items { ${lessonFields} }
    }
  }
`;

const LESSONS_BY_SLUG_WITH_CAPTIONS = /* GraphQL */ `
  query ContentLessonsBySlugWithCaptions($slug: String!, $limit: Int) {
    lessonsBySlug(slug: $slug, limit: $limit) {
      items { ${lessonPlayerFields} }
    }
  }
`;

const LESSONS_BY_SLUG_LEGACY = /* GraphQL */ `
  query ContentLessonsBySlugLegacy($slug: String!, $limit: Int) {
    lessonsBySlug(slug: $slug, limit: $limit) {
      items { ${legacyLessonPlayerFields} }
    }
  }
`;

const LESSONS_BY_SLUG_LEGACY_BASE = /* GraphQL */ `
  query ContentLessonsBySlugLegacyBase($slug: String!, $limit: Int) {
    lessonsBySlug(slug: $slug, limit: $limit) {
      items { ${legacyLessonFields} }
    }
  }
`;

const mutation = (operation, model, fields) => /* GraphQL */ `
  mutation Content${operation}${model}($input: ${operation}${model}Input!) {
    ${operation.toLowerCase()}${model}(input: $input) { ${fields} }
  }
`;

const run = async (query, variables = {}) => {
  const response = await API.graphql(graphqlOperation(query, variables));
  return response.data;
};

const listAll = async (query, rootField, filter) => {
  let nextToken = null;
  const items = [];

  do {
    const data = await run(query, { filter, limit: 1000, nextToken });
    const page = data[rootField];
    items.push(...(page?.items || []).filter(Boolean));
    nextToken = page?.nextToken || null;
  } while (nextToken);

  return items;
};

export const slugify = (value = '') =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const detectVideoProvider = (url = '') => {
  const normalized = String(url || '').toLowerCase();
  if (!normalized) return 'not set';
  if (normalized.includes('youtube.com') || normalized.includes('youtu.be')) return 'youtube';
  if (normalized.includes('vimeo.com')) return 'vimeo';
  if (normalized.includes('cloudfront.net')) return 'cloudfront';
  if (/\.(mp4|m3u8)(\?|$)/i.test(normalized)) return 'direct';
  return 'other';
};

export const isValidVideoUrl = (value = '') => {
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol);
  } catch {
    return false;
  }
};

const graphqlMessage = (error) => (error?.errors || []).map((item) => item.message).join(' ');
const missingVideoParts = (error) => /videoParts/i.test(graphqlMessage(error));

export const getLessonVideos = (lesson) => {
  let parts = lesson?.videoParts;
  try {
    if (typeof parts === 'string') parts = JSON.parse(parts);
  } catch {
    parts = null;
  }
  if (Array.isArray(parts) && parts.length) {
    const valid = parts
      .map((part, index) => ({ title: String(part?.title || '').trim() || `Part ${index + 1}`, url: String(part?.url || '').trim() }))
      .filter((part) => isValidVideoUrl(part.url));
    if (valid.length) return valid;
  }
  return [{ title: 'Full lesson', url: lesson?.videoUrl || '' }];
};

export const contentApi = {
  listInstructors: (filter) => cached(`instructors:${JSON.stringify(filter || {})}`, () => listAll(LIST_INSTRUCTORS, 'listInstructors', filter)),
  getInstructorBySlug: (slug) => cached(`instructor:${slug}`, async () => {
    const data = await run(INSTRUCTORS_BY_SLUG, { slug, limit: 1 });
    return data.instructorsBySlug?.items?.[0] || null;
  }),
  createInstructor: async (input) => { const saved = (await run(mutation('Create', 'Instructor', instructorFields), { input })).createInstructor; clearContentCache(); return saved; },
  updateInstructor: async (input) => { const saved = (await run(mutation('Update', 'Instructor', instructorFields), { input })).updateInstructor; clearContentCache(); return saved; },

  listCourses: (filter) => cached(`courses:${JSON.stringify(filter || {})}`, () => listAll(LIST_COURSES, 'listCourses', filter)),
  getCourseBySlug: (slug) => cached(`course:${slug}`, async () => {
    const data = await run(COURSES_BY_SLUG, { slug, limit: 1 });
    return data.coursesBySlug?.items?.[0] || null;
  }),
  createCourse: async (input) => { const saved = (await run(mutation('Create', 'Course', courseFields), { input })).createCourse; clearContentCache(); return saved; },
  updateCourse: async (input) => { const saved = (await run(mutation('Update', 'Course', courseFields), { input })).updateCourse; clearContentCache(); return saved; },

  listModules: (filter) => cached(`modules:${JSON.stringify(filter || {})}`, () => listAll(LIST_MODULES, 'listCourseModules', filter)),
  listModulesByCourse: (courseID) => cached(`course-modules:${courseID}`, async () => {
    const data = await run(MODULES_BY_COURSE, { courseID, sortDirection: 'ASC', limit: 1000 });
    return (data.modulesByCourse?.items || []).filter(Boolean);
  }),
  createModule: async (input) => { const saved = (await run(mutation('Create', 'CourseModule', moduleFields), { input })).createCourseModule; clearContentCache(); return saved; },
  updateModule: async (input) => { const saved = (await run(mutation('Update', 'CourseModule', moduleFields), { input })).updateCourseModule; clearContentCache(); return saved; },

  listLessons: (filter) => cached(`lessons:${JSON.stringify(filter || {})}`, async () => {
    try { return await listAll(LIST_LESSONS, 'listLessons', filter); }
    catch (error) { if (!missingVideoParts(error)) throw error; return listAll(LIST_LESSONS_LEGACY, 'listLessons', filter); }
  }),
  listLessonsByCourse: (courseID) => cached(`course-lessons:${courseID}`, async () => {
    const variables = { courseID, sortDirection: 'ASC', limit: 1000 };
    let data;
    try { data = await run(LESSONS_BY_COURSE, variables); }
    catch (error) { if (!missingVideoParts(error)) throw error; data = await run(LESSONS_BY_COURSE_LEGACY, variables); }
    return (data.lessonsByCourse?.items || []).filter(Boolean);
  }),
  getLessonBySlug: (slug) => cached(`lesson:${slug}`, async () => {
    let data;
    try {
      data = await run(LESSONS_BY_SLUG_WITH_CAPTIONS, { slug, limit: 1 });
    } catch (error) {
      if (missingVideoParts(error)) {
        try { data = await run(LESSONS_BY_SLUG_LEGACY, { slug, limit: 1 }); }
        catch (legacyError) {
          if (!/captionUrl|captionLabel/i.test(graphqlMessage(legacyError))) throw legacyError;
          data = await run(LESSONS_BY_SLUG_LEGACY_BASE, { slug, limit: 1 });
        }
      } else {
        if (!/captionUrl|captionLabel/i.test(graphqlMessage(error))) throw error;
        data = await run(LESSONS_BY_SLUG, { slug, limit: 1 });
      }
    }
    return data.lessonsBySlug?.items?.[0] || null;
  }),
  createLesson: async (input) => { const saved = (await run(mutation('Create', 'Lesson', lessonFields), { input })).createLesson; clearContentCache(); return saved; },
  updateLesson: async (input) => { const saved = (await run(mutation('Update', 'Lesson', lessonFields), { input })).updateLesson; clearContentCache(); return saved; },
};
